import { HTMLAttributes, FC, memo, useMemo, MouseEvent } from 'react';
import cn from 'classnames';

import useRenderType from '@Hooks/useRenderType';
import ProductTags from '@Components/ProductTags';
import ProductLike from '@Components/ProductLike';
import Characteristics from '@Components/Characteristics';
import Price from '@UI/Price';
import Discount from '@UI/Discount';
import Image from '@UI/Image';
import ProductNoImage from '@UI/ProductNoImage';
import BuyButton from '@UI/BuyButton';
import Link from '@Navigation/Link';
import { ProductData, ProductGroups } from '@Types/Product';
import getProductName from '@Utils/getProductName';
import styles from './CrossSaleProductCard.module.css';

export interface CrossSaleProductCardProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  product: ProductData;
  hiddenCharacteristics?: boolean;
  hiddenBuyLink?: boolean;
  onClickProductLink?: (e: MouseEvent) => void;
}

const CrossSaleProductCard: FC<CrossSaleProductCardProps> = (props) => {
  const {
    className,
    product,
    hiddenCharacteristics,
    hiddenBuyLink,
    onClickProductLink,
    ...restProps
  } = props;
  const { isCSR, isSSR } = useRenderType();
  const hasExpired = product.price.expired > 0;
  const hasDiscount = product.price.discount > 0;
  const [firstImage] = product.images;
  const orientation = product.orientation || 'landscape';

  const separatedProductGroups = useMemo<ProductGroups>(() => {
    const groups: ProductGroups = { default: [], secondary: [] };

    product.parameterGroups.forEach((item) => {
      if (!groups[item.place]) groups[item.place] = [];

      groups[item.place].push(item);
    });

    return groups;
  }, [product.parameterGroups]);

  return (
    <div
      {...restProps}
      className={cn(
        styles.card,
        {
          [styles.promo]: product.promo,
        },
        className,
      )}
    >
      <div className={styles.img}>
        <Link to={product.link} onClick={onClickProductLink}>
          {firstImage ? (
            <Image
              className={cn(styles.image, {
                [styles.landscape]: orientation === 'landscape',
                [styles.portrait]: orientation === 'portrait',
              })}
              cnImage={styles.cnImage}
              src={firstImage.src}
              alt={getProductName(product)}
            />
          ) : (
            <ProductNoImage className={styles.noImage} />
          )}
        </Link>

        <div className={styles.actions}>
          <ProductLike product={product} />
        </div>
        {product.tags?.length > 0 && <ProductTags className={styles.tags} tags={product.tags} />}
      </div>
      <div className={styles.info}>
        <Link className={styles.name} to={product.link} onClick={onClickProductLink}>
          {getProductName(product)}
        </Link>
        <div className={styles.price}>
          {/* Требование SEO чтобы от сервера всегда приходила цена,
            и только на клиенте изменялась на Нет в наличии */}
          {isCSR && !product.isActive ? (
            <div className={styles.outOfStock}>Нет в наличии</div>
          ) : (
            <>
              <Price className={styles.actualPrice} price={product.price.actual} />
              &nbsp;
              {hasExpired && (
                <Price expired className={styles.expiredPrice} price={product.price.expired} />
              )}
              {hasDiscount && (
                <Discount size='s' className={styles.discount}>
                  {`-${product.price.discount}%`}
                </Discount>
              )}
            </>
          )}
        </div>
        {separatedProductGroups.default.length > 0 && (
          <Characteristics
            className={styles.parameters}
            groups={separatedProductGroups.default}
            parameterValues={product.parameterValues}
            parameters={product.parameters}
            units={product.units}
          />
        )}

        {/* По требованию SEO в некоторых местах сайта (например на странице категорий) необходимо получать в разметке информацию о характеристиках */}
        {hiddenCharacteristics && isSSR && (
          <>
            {separatedProductGroups.secondary?.length > 0 && (
              <Characteristics
                className={styles.hiddenCharacteristics}
                groups={separatedProductGroups.secondary}
                parameterValues={product.parameterValues}
                parameters={product.parameters}
                units={product.units}
              />
            )}
          </>
        )}

        {/* По требованию SEO в некоторых местах сайта (например на странице категорий) необходимо получать разметку кнопки купить */}
        {hiddenBuyLink && isSSR && <BuyButton href={product.link} />}
      </div>
    </div>
  );
};

export default memo(CrossSaleProductCard);
